/**
 * Admin-only activity timeline. Two sections:
 *   - Summary cards: total events, top actions, top users (last N hours)
 *   - Event timeline: paginated table of user/action/path/time
 *
 * Backed by /api/admin/activity (timeline) + /api/admin/activity/summary.
 * Only visible to admin accounts — `_get_current_admin` enforces 403.
 */
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import AdminHubNav from "../components/AdminHubNav";
import { api, friendlyAxiosError } from "../lib/api";
import { Activity, Filter, Loader, RefreshCw, Users, BarChart3 } from "lucide-react";

const WINDOWS = [
  { id: 1,   label: "Last 1h" },
  { id: 24,  label: "Last 24h" },
  { id: 168, label: "Last 7d" },
  { id: 720, label: "Last 30d" },
];

const ACTION_LABELS = {
  page_view: "Pages",
  register: "Registered",
  resume_upload: "Resume upload",
  analyze_start: "Tailor start",
  analyze_complete: "Tailor done",
  skills_assessment: "Skill Lab",
  role_research: "Role research",
  mock_interview_start: "Mock start",
  mock_interview_complete: "Mock done",
  studio_open: "Studio",
  studio_save: "Studio save",
  pitch_generate: "Pitch",
  avatar_generate: "Avatar",
  mindset_call: "Mindset",
  events_suggest: "Communities",
  contact_add: "Contact add",
  contact_analyze: "Contact analyze",
  tour_open: "Tour",
  quickstart_door_picked: "Quickstart",
};

function DayReport({ BetaWeekBadge }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // email of expanded error row

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/activity/day-report", { params: { date } })
      .then((r) => { if (alive) setReport(r.data); })
      .catch(() => { if (alive) setReport(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date]);

  const hhmm = (iso) => {
    try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const testers = (report?.testers || []).filter((t) => !t.is_admin);
  const admins = (report?.testers || []).filter((t) => t.is_admin);
  const orphans = report?.orphan_errors || [];

  return (
    <div className="rm-card-static p-4 mb-5" data-testid="day-report-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="rm-tag" data-testid="day-report-eyebrow">📊 Beta day report</div>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rm-input text-xs px-2 py-1.5"
          style={{ maxWidth: 160 }}
          data-testid="day-report-date"
        />
        {report && (
          <div className="ml-auto flex gap-2 font-mono-rm text-[10px] uppercase tracking-widest">
            <span className="px-2 py-1 rounded" style={{ background: "#EFF6FF", color: "#1E40AF", fontWeight: 700 }}
                  data-testid="day-report-total-testers">
              {report.totals.active_users} tester{report.totals.active_users === 1 ? "" : "s"}
            </span>
            <span className="px-2 py-1 rounded" style={{ background: "#F1F5F9", color: "#334155", fontWeight: 700 }}>
              {report.totals.events} events
            </span>
            <span className="px-2 py-1 rounded"
                  style={{ background: report.totals.errors ? "#FEF2F2" : "#D1FAE5",
                           color: report.totals.errors ? "#B91C1C" : "#0E5A4A", fontWeight: 700 }}
                  data-testid="day-report-total-errors">
              {report.totals.errors} error{report.totals.errors === 1 ? "" : "s"}
            </span>
            {report.helper?.questions > 0 && (
              <span className="px-2 py-1 rounded"
                    style={{ background: "#FFF7ED", color: "#9A3412", fontWeight: 700 }}
                    data-testid="day-report-total-helper">
                🤖 {report.helper.questions} helper Q{report.helper.questions === 1 ? "" : "s"}
              </span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="font-mono-rm text-xs py-4" style={{ color: "#64748B" }}>Loading the day…</div>
      ) : testers.length === 0 && orphans.length === 0 ? (
        <div className="font-mono-rm text-xs py-4" style={{ color: "#64748B" }} data-testid="day-report-empty">
          No tester activity on {date}.{admins.length > 0 ? ` (${admins.length} admin account${admins.length > 1 ? "s" : ""} active.)` : ""}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="day-report-table">
            <thead>
              <tr className="font-mono-rm text-[9px] uppercase tracking-widest" style={{ color: "#64748B" }}>
                <th className="py-1.5 pr-3">Tester</th>
                <th className="py-1.5 pr-3">Sign-ins</th>
                <th className="py-1.5 pr-3">In app</th>
                <th className="py-1.5 pr-3">Features touched</th>
                <th className="py-1.5">Errors</th>
              </tr>
            </thead>
            <tbody>
              {testers.map((t) => (
                <React.Fragment key={t.email}>
                  <tr className="border-t align-top" style={{ borderColor: "#E2E8F0" }}
                      data-testid={`day-report-row-${t.email}`}>
                    <td className="py-2 pr-3">
                      <div className="text-sm font-bold" style={{ color: "#0F1B2D" }}>{t.name || "—"}</div>
                      <div className="font-mono-rm text-[10px]" style={{ color: "#64748B" }}>
                        {t.email}<BetaWeekBadge week={t.beta_week} />
                      </div>
                    </td>
                    <td className="py-2 pr-3 font-mono-rm text-sm">{t.sign_ins || "—"}</td>
                    <td className="py-2 pr-3 font-mono-rm text-[11px]" style={{ color: "#334155" }}>
                      {hhmm(t.first_seen)} – {hhmm(t.last_seen)}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(t.actions)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 8)
                          .map(([act, n]) => (
                            <span key={act}
                                  className="font-mono-rm text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                                  style={{ background: "#FFF7ED", color: "#9A3412", border: "1px solid #FDBA74", fontWeight: 700 }}>
                              {(ACTION_LABELS[act] || act.replace("custom:", "")).slice(0, 18)} ×{n}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td className="py-2">
                      {t.errors.count ? (
                        <button
                          onClick={() => setExpanded(expanded === t.email ? null : t.email)}
                          className="font-mono-rm text-[10px] px-2 py-0.5 rounded"
                          style={{ background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5", fontWeight: 700 }}
                          data-testid={`day-report-errors-${t.email}`}>
                          ⚠ {t.errors.count} {expanded === t.email ? "▲" : "▼"}
                        </button>
                      ) : (
                        <span className="font-mono-rm text-[10px]" style={{ color: "#0E5A4A", fontWeight: 700 }}>✓ clean</span>
                      )}
                    </td>
                  </tr>
                  {expanded === t.email && t.errors.recent.map((e, i) => (
                    <tr key={i} style={{ background: "#FEF2F2" }}>
                      <td colSpan={5} className="py-1.5 px-3 font-mono-rm text-[10px]" style={{ color: "#991B1B" }}>
                        {hhmm(e.at)} · {e.path || "?"} · {e.status ?? "net"} · {e.message}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {orphans.length > 0 && (
            <div className="mt-3 p-2 rounded font-mono-rm text-[10px]"
                 style={{ background: "#FEF2F2", border: "1px dashed #FCA5A5", color: "#991B1B" }}
                 data-testid="day-report-orphan-errors">
              Pre-login errors: {orphans.map((o) => `${o.email} ×${o.count}`).join(" · ")}
            </div>
          )}
          {report?.helper?.sessions > 0 && (
            <div className="mt-3 p-2.5 rounded"
                 style={{ background: "#FFF7ED", border: "1px dashed #FDBA74" }}
                 data-testid="day-report-helper-block">
              <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-1.5"
                   style={{ color: "#9A3412", fontWeight: 700 }}>
                🤖 Helper conversations · {report.helper.sessions} session{report.helper.sessions === 1 ? "" : "s"}
                {report.helper.error_triggered > 0 && ` · ${report.helper.error_triggered} error-triggered`}
              </div>
              {report.helper.conversations.map((c) => (
                <div key={c.session_id} className="font-mono-rm text-[10px] py-0.5" style={{ color: "#78350F" }}
                     data-testid={`day-report-helper-${c.session_id}`}>
                  {c.error_triggered ? "⚠ " : ""}{c.email} · {c.questions} Q{c.questions === 1 ? "" : "s"} · “{c.first_question}{c.first_question.length >= 120 ? "…" : ""}”
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminActivity() {
  const [hours, setHours] = useState(168);
  const [action, setAction] = useState("");
  const [userId, setUserId] = useState("");
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const reload = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, e] = await Promise.all([
        api.get(`/admin/activity/summary?hours=${hours}`),
        api.get(`/admin/activity`, { params: { hours, action: action || undefined, user_id: userId || undefined, limit: 300 } }),
      ]);
      setSummary(s.data);
      setEvents(e.data.events || []);
    } catch (er) {
      setErr(friendlyAxiosError(er));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [hours]);

  const topActions = summary?.actions || [];
  const topUsers = summary?.users || [];

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch { return iso; }
  };

  const BetaWeekBadge = ({ week }) => {
    if (!week) return null;
    const over = week > 4;
    return (
      <span
        className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ml-1 inline-block"
        style={{
          background: over ? "#FEE2E2" : "#EFF6FF",
          color: over ? "#991B1B" : "#1E40AF",
          border: `1px solid ${over ? "#FCA5A5" : "#BFDBFE"}`,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
        title={over ? `Past the 4-week beta window (week ${week})` : `Beta week ${week} of 4 (from NDA signing / registration)`}
        data-testid="beta-week-badge"
      >
        {over ? `Wk ${week} · over` : `Wk ${week}/4`}
      </span>
    );
  };

  return (
    <div className="min-h-screen rm-paper pb-20">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <AdminHubNav active="activity" />
        <div className="rm-tag mb-3" data-testid="activity-eyebrow">
          <Activity size={12} /> Admin · Activity
        </div>
        <h1 className="font-display text-4xl uppercase mb-2">Who's been here.</h1>
        <p className="text-sm mb-6" style={{ color: "#475569" }}>
          Real-time event log of every user action — page views, AI generations, contact adds, mock interviews. Mirrored to PostHog when configured.
        </p>

        <DayReport BetaWeekBadge={BetaWeekBadge} />

        {/* Filter bar */}
        <div className="rm-card-static p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="activity-filters">
          <div className="flex gap-1">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => setHours(w.id)}
                className="font-mono-rm text-[10px] uppercase tracking-widest px-2.5 py-1.5"
                style={{
                  background: hours === w.id ? "#0F1B2D" : "white",
                  color: hours === w.id ? "#FFC145" : "#0F1B2D",
                  border: "2px solid #0F1B2D",
                  borderRadius: 4,
                  fontWeight: 700,
                }}
                data-testid={`activity-window-${w.id}`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="rm-label block mb-0.5">Action filter</label>
            <input className="rm-input" placeholder="e.g. pitch_generate" value={action}
                   onChange={(e) => setAction(e.target.value)}
                   data-testid="activity-action-filter" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="rm-label block mb-0.5">User id</label>
            <input className="rm-input" placeholder="paste user.id" value={userId}
                   onChange={(e) => setUserId(e.target.value)}
                   data-testid="activity-user-filter" />
          </div>
          <button onClick={reload} disabled={loading}
                  className="rm-btn rm-btn-primary text-xs px-3 py-2"
                  data-testid="activity-reload">
            {loading ? <Loader size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Refresh
          </button>
        </div>

        {err && (
          <div className="p-3 rounded-md font-mono-rm text-sm mb-4"
               style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}>
            {err}
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-5">
          <div className="md:col-span-3 rm-card-static p-4" data-testid="activity-total-card">
            <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>· Total events</div>
            <div className="font-display text-4xl uppercase">{summary?.total_events ?? "—"}</div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-1" style={{ color: "#94A3B8" }}>
              window: {hours}h
            </div>
          </div>
          <div className="md:col-span-5 rm-card-static p-4" data-testid="activity-top-actions">
            <div className="rm-label mb-2" style={{ color: "#FF6A00" }}><BarChart3 size={11} className="inline" /> · Top actions</div>
            <ul className="text-sm space-y-1">
              {topActions.length === 0 ? <li className="text-xs" style={{ color: "#475569" }}>—</li> : null}
              {topActions.slice(0, 8).map((a, i) => (
                <li key={i} className="flex justify-between gap-2 items-center">
                  <button
                    onClick={() => setAction(a.action)}
                    className="font-mono-rm text-[11px] underline"
                    style={{ color: "#0F1B2D" }}
                    title="Filter timeline by this action"
                  >
                    {a.action}
                  </button>
                  <span className="font-mono-rm text-[11px]" style={{ color: "#475569" }}>
                    {a.count} ev · {a.user_count} users
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4 rm-card-static p-4" data-testid="activity-top-users">
            <div className="rm-label mb-2" style={{ color: "#FF6A00" }}><Users size={11} className="inline" /> · Most-active users</div>
            <ul className="text-sm space-y-1">
              {topUsers.length === 0 ? <li className="text-xs" style={{ color: "#475569" }}>—</li> : null}
              {topUsers.slice(0, 8).map((u, i) => (
                <li key={i} className="flex justify-between gap-2 items-center">
                  <span className="truncate" style={{ maxWidth: 210 }}>
                    <button
                      onClick={() => setUserId(u.user_id)}
                      className="text-[11px] underline"
                      style={{ color: "#0F1B2D" }}
                      title="Filter timeline by this user"
                    >
                      {u.user?.email || u.user_id}
                    </button>
                    <BetaWeekBadge week={u.user?.beta_week} />
                  </span>
                  <span className="font-mono-rm text-[11px]" style={{ color: "#475569" }}>{u.count} ev</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Event timeline */}
        <div className="rm-card-static p-0 overflow-hidden" data-testid="activity-timeline">
          <div className="px-4 py-3" style={{ background: "#0F1B2D", color: "white" }}>
            <div className="flex items-center gap-2">
              <Filter size={13} style={{ color: "#FFC145" }} />
              <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#FFC145", fontWeight: 700 }}>
                Timeline · {events.length} events shown
              </div>
            </div>
          </div>
          {loading ? (
            <div className="p-6 text-center font-mono-rm text-xs">Loading…</div>
          ) : events.length === 0 ? (
            <div className="p-6 text-center font-mono-rm text-xs" style={{ color: "#475569" }}>
              No events in this window.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: "#FFFBEB" }}>
                  <tr style={{ borderBottom: "2px solid #0F1B2D" }}>
                    <th className="text-left p-2.5 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#92400E" }}>When</th>
                    <th className="text-left p-2.5 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#92400E" }}>Who</th>
                    <th className="text-left p-2.5 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#92400E" }}>Action</th>
                    <th className="text-left p-2.5 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#92400E" }}>Where</th>
                    <th className="text-left p-2.5 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#92400E" }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} style={{ borderBottom: "1px solid #E2E8F0" }}>
                      <td className="p-2.5 font-mono-rm text-[11px]" style={{ color: "#475569", whiteSpace: "nowrap" }}>
                        {fmt(e.created_at)}
                      </td>
                      <td className="p-2.5 text-xs">
                        <div className="font-semibold">
                          {e.user?.name || e.user?.email || "—"}
                          <BetaWeekBadge week={e.user?.beta_week} />
                        </div>
                        <div className="font-mono-rm text-[10px]" style={{ color: "#94A3B8" }}>{e.user?.email}</div>
                      </td>
                      <td className="p-2.5 font-mono-rm text-[11px]" style={{ color: "#0F1B2D", fontWeight: 700 }}>{e.action}</td>
                      <td className="p-2.5 font-mono-rm text-[11px]" style={{ color: "#475569" }}>{e.path || "—"}</td>
                      <td className="p-2.5 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: e.source === "frontend" ? "#FF6A00" : "#10B981", fontWeight: 700 }}>
                        {e.source}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
